import React from "react";
import { Label } from "../../../components/ui/label";
import { Input } from "../../../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../../components/ui/select";
import AssetVersionSlotField from "../components/AssetVersionSlotField";
import { COMPOSITION_SLOTS } from "./compositionSlots";

/**
 * CreativeCompositionFields — EMAILS-IMPL-011
 *
 * All 9 governed composition slots (via AssetVersionSlotField, one per
 * COMPOSITION_SLOTS entry) plus the Image Presentation sub-form, which
 * only renders once an Image is actually selected (CRE-SPEC-001 §14:
 * placement/width/alignment are only meaningful "where referenced").
 * Shared between the Create Creative and Create New Version dialogs --
 * the exact same 9 slots and validation apply to both.
 */
export default function CreativeCompositionFields({ composition, onChange, testPrefix }) {
  const setSlot = (key, value) => onChange({ ...composition, [key]: value });

  return (
    <div className="space-y-3">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-400">Composition</div>
      {COMPOSITION_SLOTS.map((slot) => (
        <AssetVersionSlotField
          key={slot.key}
          label={slot.label}
          required={slot.required}
          value={composition[slot.key]}
          onChange={(value) => setSlot(slot.key, value)}
          listFn={slot.listFn}
          versionsFn={slot.versionsFn}
          idField={slot.idField}
          eligibleStatuses={slot.eligibleStatuses}
          testPrefix={`${testPrefix}-${slot.key}`}
        />
      ))}

      {composition.image && (
        <ImagePresentationFields
          value={composition.imagePresentation}
          onChange={(imagePresentation) => onChange({ ...composition, imagePresentation })}
          testPrefix={testPrefix}
        />
      )}
    </div>
  );
}

function ImagePresentationFields({ value, onChange, testPrefix }) {
  const v = value || { placement: "", width: "", alignment: "" };
  const update = (patch) => onChange({ ...v, ...patch });

  return (
    <div className="space-y-3 rounded-md border border-white/10 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-400">Image Presentation (optional)</div>
      <p className="text-xs text-slate-500">
        How this Image appears in THIS Creative -- never changes the Image Asset itself.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor={`${testPrefix}-image-placement-input`}>Placement</Label>
          <Input
            id={`${testPrefix}-image-placement-input`}
            value={v.placement || ""}
            onChange={(e) => update({ placement: e.target.value })}
            placeholder="e.g. header"
            data-testid={`${testPrefix}-image-placement-input`}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${testPrefix}-image-width-input`}>Display Width (px)</Label>
          <Input
            id={`${testPrefix}-image-width-input`}
            type="number"
            value={v.width ?? ""}
            onChange={(e) => update({ width: e.target.value })}
            placeholder="e.g. 600"
            data-testid={`${testPrefix}-image-width-input`}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${testPrefix}-image-alignment-select`}>Alignment</Label>
          <Select value={v.alignment || ""} onValueChange={(alignment) => update({ alignment })}>
            <SelectTrigger id={`${testPrefix}-image-alignment-select`} data-testid={`${testPrefix}-image-alignment-select`}>
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="left" data-testid={`${testPrefix}-image-alignment-option-left`}>Left</SelectItem>
              <SelectItem value="center" data-testid={`${testPrefix}-image-alignment-option-center`}>Center</SelectItem>
              <SelectItem value="right" data-testid={`${testPrefix}-image-alignment-option-right`}>Right</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
